/**
 * Whether a product is visible in the catalog for a given language version.
 *
 * enable_catalog is '0' (none), '1' (all versions), or a ';'-separated list of
 * version identifiers (e.g. 'fi_FI;en_US').
 *
 * @param {string} enableCatalog  The product's enable_catalog value
 * @param {string} version        The version identifier to check against
 * @returns {boolean}
 */
export function isProductVisibleForVersion (enableCatalog, version) {
  if (!enableCatalog || enableCatalog === '0') {
    return false
  }
  if (enableCatalog === '1') {
    return true
  }
  return String(enableCatalog).split(';').includes(version)
}

/**
 * Whether a product is intentionally private (enable_collection set).
 *
 * Such products are deliberately used in widgets as private/collection-only
 * products with no catalog listing, so they should be treated as ok.
 *
 * @param {string} enableCollection  The product's enable_collection value
 * @returns {boolean}
 */
export function isCollectionEnabled (enableCollection) {
  return !!enableCollection && enableCollection !== '0'
}

/**
 * Whether a category is visible in widgets for a given language version.
 *
 * A category is OK when enable_widgets is set, enable_collection is set
 * (intentionally private), or enable_catalog matches the version.
 *
 * @param {object} category  Category object with enable_catalog, enable_widgets, enable_collection
 * @param {string} version   The version identifier to check against
 * @returns {boolean}
 */
export function isCategoryVisibleInWidgets (category, version) {
  if (!category) {
    return false
  }
  if (parseInt(category.enable_widgets, 10) === 1) {
    return true
  }
  if (isCollectionEnabled(category.enable_collection)) {
    return true
  }
  return isProductVisibleForVersion(category.enable_catalog, version)
}

/**
 * Merge a version into an enable_catalog value so the product becomes visible
 * for that version, preserving any versions it is already published for.
 *
 * @param {string} enableCatalog  The product's current enable_catalog value
 * @param {string} version        The version identifier to add
 * @returns {string}              The new enable_catalog value
 */
export function addVersionToEnableCatalog (enableCatalog, version) {
  if (enableCatalog === '1') {
    return '1'
  }
  const versions = (!enableCatalog || enableCatalog === '0')
    ? []
    : String(enableCatalog).split(';').filter(v => v)
  if (!versions.includes(version)) {
    versions.push(version)
  }
  return versions.join(';')
}
